import { useState, useEffect } from 'react';
import { 
  FileVideo, 
  FileImage, 
  FileText, 
  FileAudio, 
  File, 
  Play, 
  X,
  Shield
} from 'lucide-react';
import apiClient from '../../api/client';

interface FilePreviewProps {
  fileName: string;
  fileType: string;
  thumbnailUrl?: string;
  externalUrl?: string;
  fileSize?: number;
  duration?: string;
  variant?: 'card' | 'compact' | 'detailed';
  onClick?: () => void;
  className?: string;
}

const fileTypeIcons: Record<string, typeof File> = {
  video: FileVideo,
  image: FileImage,
  document: FileText,
  audio: FileAudio,
  default: File,
};

function getFileCategory(fileType: string): string {
  const type = fileType.toLowerCase();
  if (type.includes('video') || type.includes('mp4') || type.includes('mov')) return 'video';
  if (type.includes('image') || type.includes('jpg') || type.includes('png') || type.includes('jpeg')) return 'image';
  if (type.includes('audio') || type.includes('mp3') || type.includes('wav')) return 'audio';
  if (type.includes('pdf') || type.includes('doc') || type.includes('document')) return 'document';
  return 'default';
}

function formatFileSize(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
}

export function FilePreview({
  fileName,
  fileType,
  thumbnailUrl,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  externalUrl: _externalUrl,
  fileSize,
  duration,
  variant = 'card',
  onClick,
  className = '',
}: FilePreviewProps) {
  const category = getFileCategory(fileType);
  const Icon = fileTypeIcons[category];

  if (variant === 'compact') {
    return (
      <div 
        className={`flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer ${className}`}
        onClick={onClick}
      >
        <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
          {thumbnailUrl ? (
            <img 
              src={thumbnailUrl} 
              alt={fileName} 
              className="w-full h-full object-cover rounded"
            />
          ) : (
            <Icon className="h-5 w-5 text-gray-400" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{fileName}</p>
          <p className="text-xs text-gray-500">
            {formatFileSize(fileSize)}
            {duration && ` • ${duration}`}
          </p>
        </div>
      </div>
    );
  }

  if (variant === 'detailed') {
    return (
      <div className={`bg-white rounded-xl shadow-sm overflow-hidden ${className}`}>
        <div className="aspect-video bg-gray-900 relative">
          {thumbnailUrl ? (
            <img 
              src={thumbnailUrl} 
              alt={fileName} 
              className="w-full h-full object-contain"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <Icon className="h-16 w-16 text-gray-600" />
            </div>
          )}
          {category === 'video' && (
            <div className="absolute inset-0 flex items-center justify-center">
              <button 
                onClick={onClick}
                className="p-4 bg-white/90 rounded-full shadow-lg hover:bg-white transition-colors"
              >
                <Play className="h-8 w-8 text-gray-900" />
              </button>
            </div>
          )}
          {duration && (
            <span className="absolute bottom-2 right-2 px-2 py-1 bg-black/70 text-white text-xs rounded">
              {duration}
            </span>
          )}
        </div>
        <div className="p-4">
          <p className="font-medium text-gray-900 truncate">{fileName}</p>
          <p className="text-sm text-gray-500 mt-1">
            {fileType} • {formatFileSize(fileSize)}
          </p>
        </div>
      </div>
    );
  }

  // Default card variant
  return (
    <div 
      className={`bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow cursor-pointer ${className}`}
      onClick={onClick}
    >
      <div className="aspect-video bg-gray-100 relative">
        {thumbnailUrl ? (
          <img 
            src={thumbnailUrl} 
            alt={fileName} 
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Icon className="h-12 w-12 text-gray-400" />
          </div>
        )}
        {category === 'video' && (
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
            <Play className="h-10 w-10 text-white" />
          </div>
        )}
        {duration && (
          <span className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/70 text-white text-xs rounded">
            {duration}
          </span>
        )}
      </div>
      <div className="p-3">
        <p className="text-sm font-medium text-gray-900 truncate">{fileName}</p>
        <p className="text-xs text-gray-500 mt-0.5">
          {formatFileSize(fileSize)}
        </p>
      </div>
    </div>
  );
}

/**
 * Modal for viewing evidence files
 */
interface EvidenceViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  evidenceId?: string;
  fileName: string;
  fileType: string;
  externalUrl?: string;
}

export function EvidenceViewerModal({
  isOpen,
  onClose,
  evidenceId,
  fileName,
  fileType,
  externalUrl,
}: EvidenceViewerModalProps) {
  const [viewLogged, setViewLogged] = useState(false);
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);

  // Log view event and fetch preview URL when modal opens
  useEffect(() => {
    if (isOpen && evidenceId && !viewLogged) {
      apiClient.logViewEvent(evidenceId)
        .then(() => setViewLogged(true))
        .catch(err => console.error('Failed to log view event:', err));
    }
    // Fetch blob URL when no externalUrl is provided
    if (isOpen && evidenceId && !externalUrl && !resolvedUrl && !previewLoading) {
      setPreviewLoading(true);
      setPreviewError(false);
      apiClient.getPreviewBlobUrl(evidenceId)
        .then(url => setResolvedUrl(url))
        .catch(() => setPreviewError(true))
        .finally(() => setPreviewLoading(false));
    }
    // Reset state when modal closes
    if (!isOpen) {
      setViewLogged(false);
      if (resolvedUrl) {
        URL.revokeObjectURL(resolvedUrl);
        setResolvedUrl(null);
      }
      setPreviewError(false);
    }
  }, [isOpen, evidenceId, viewLogged]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  const category = getFileCategory(fileType);
  const displayUrl = externalUrl || resolvedUrl;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/80" onClick={onClose} />
      
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* View logged notification */}
          {viewLogged && (
            <div className="absolute top-16 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-full shadow-lg animate-fade-in">
              <Shield className="h-4 w-4" />
              <span>Your view has been logged in the chain of custody</span>
            </div>
          )}

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h3 className="font-medium text-gray-900 truncate">{fileName}</h3>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          {/* Content */}
          <div className="bg-gray-900 flex items-center justify-center" style={{ height: '70vh' }}>
            {previewLoading ? (
              <div className="text-center text-gray-400">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white mx-auto mb-4" />
                <p>Loading preview...</p>
              </div>
            ) : category === 'video' && displayUrl ? (
              <video
                src={displayUrl}
                controls
                className="max-w-full max-h-full"
                autoPlay
              />
            ) : category === 'image' && displayUrl ? (
              <img
                src={displayUrl}
                alt={fileName}
                className="max-w-full max-h-full object-contain"
              />
            ) : category === 'document' && displayUrl ? (
              <iframe
                src={displayUrl}
                className="w-full h-full bg-white"
                title={fileName}
              />
            ) : category === 'audio' && displayUrl ? (
              <audio src={displayUrl} controls className="w-full max-w-md" autoPlay />
            ) : (
              <div className="text-center text-gray-400">
                <File className="h-16 w-16 mx-auto mb-4" />
                <p>{previewError ? 'Preview unavailable' : 'Preview not available'}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default FilePreview;
